import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, Flex, Text } from '@chakra-ui/react';
import {
  NOTIFICATION_TYPE,
  NOTIFICATION_TYPE_TOPIC_MAP,
  PLATFORM,
  SUBSCRIPTION_STATUS_TYPE,
} from '../../../../../constants/benchat';
import { isInAppBrowser } from '../../../../../service/browserService';
import { useAsyncSubscription } from '../../../../../hooks';
import pnotifyService from '../../../../../service/pnotifyService';
import benchatService from '../../../../../service/benchatService';
import trackerService from '../../../../../service/trackerService';
import {
  EVENT_NAME_FOR_SUBSCRIPTION,
  EVENT_CATEGORY_FOR_SUBSCRIPTION,
  EVENT_PROPERTY_FOR_SUBSCRIPTION,
  EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION,
} from '../../../../../shop/constants/tracker';
import getPopupWindowSize from '../../../../../utils/getPopupWindowSize';
import { CheckCircle, Line } from '../../../../context/Chakra/Icons';
import { useAppContext } from '../../../context/appContext';

const LineSubscription = (props) => {
  const { e2eId, subscriptions, notificationType, shouldCheckMemberReward } =
    props;
  const { currentUser, lineSubscriptionConfig } = useAppContext();
  const { channel } = lineSubscriptionConfig;
  const userId = currentUser._id;
  const { t } = useTranslation();
  const refPopupWindow = useRef();
  const {
    hasSubscribed,
    startPolling,
    subscriptionStatus,
    unsubscribe,
    userRef,
    checkMemberRewardForLINEOrFB,
  } = useAsyncSubscription({
    userId,
    platform: PLATFORM.LINE_MES_API,
    subscriptions,
    topics: NOTIFICATION_TYPE_TOPIC_MAP[notificationType],
    notificationType,
  });

  useEffect(() => {
    switch (subscriptionStatus) {
      case SUBSCRIPTION_STATUS_TYPE.SUCCESS:
        if (shouldCheckMemberReward) checkMemberRewardForLINEOrFB(userId);
        pnotifyService.notify(t('member_profile.subscribe.successfully'));
        break;
      case SUBSCRIPTION_STATUS_TYPE.SOCIAL_USER_BIND_ERROR:
        pnotifyService.notify(t('member_profile.subscribe.error'), {
          customClass: 'error',
          icon: 'fa fa-exclamation-triangle',
        });
        break;
      case SUBSCRIPTION_STATUS_TYPE.SOCIAL_USER_DOUBLE_BIND_ERROR:
        pnotifyService.notify(
          t('member_profile.social.subscribe.error.already_bound_to'),
          {
            customClass: 'error',
            icon: 'fa fa-exclamation-triangle',
          },
        );
        break;
      case SUBSCRIPTION_STATUS_TYPE.PLATFORM_USER_ID_SIGNED_UP_BY_OTHERS_ERROR:
        pnotifyService.notify(
          t('member_profile.line.subscribe.error.already_for_registration'),
          {
            customClass: 'error',
            icon: 'fa fa-exclamation-triangle',
          },
        );
        break;
      case SUBSCRIPTION_STATUS_TYPE.PLATFORM_ID_DIFFERENT_ERROR:
        pnotifyService.notify(
          t('member_profile.line.subscribe.error.different_id'),
          {
            customClass: 'error',
            icon: 'fa fa-exclamation-triangle',
          },
        );
        break;
      // compatible with polling logic adjustment
      case SUBSCRIPTION_STATUS_TYPE.NO_RECORD:
      case SUBSCRIPTION_STATUS_TYPE.UNKNOWN_ERROR:
        pnotifyService.notify(t('member_profile.line.subscribe.error'), {
          customClass: 'error',
          icon: 'fa fa-exclamation-triangle',
        });
        break;
      default:
        break;
    }
  }, [
    subscriptionStatus,
    t,
    checkMemberRewardForLINEOrFB,
    userId,
    shouldCheckMemberReward,
  ]);

  const handleUnsubscribe = async () => {
    const result = await unsubscribe();
    if (result === SUBSCRIPTION_STATUS_TYPE.SUCCESS) {
      pnotifyService.notify(t('member_profile.unsubscribe.successfully'));
    } else {
      pnotifyService.notify(t('member_profile.unsubscribe.error'), {
        customClass: 'error',
        icon: 'fa fa-exclamation-triangle',
      });
    }
  };

  const handleSubscribe = async () => {
    trackerService.track({
      type: EVENT_NAME_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION_BUTTON_CLICK,
      data: {
        event_category: EVENT_CATEGORY_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.SUBSCRIPTION_TYPE]:
          notificationType === NOTIFICATION_TYPE.MARKETING
            ? EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.PROMOTION_NOTIFICATION
            : EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.ORDER_NOTIFICATION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.SUBSCRIPTION_PATH]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.MEMBER_CENTER,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.THIRD_PARTY]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.LINE,
      },
    });

    if (isInAppBrowser()) {
      const url = benchatService.getLineMessageSubscriptionRedirectToUri(
        channel.platformChannelId,
        userRef.current,
        notificationType,
      );
      window.location.replace(
        `${url}&shopRedirectUri=${window.location.pathname}`,
      );
    } else {
      refPopupWindow.current = window.open(
        benchatService.getLineMessageSubscriptionPopupUri(
          channel.platformChannelId,
          userRef.current,
          notificationType,
        ),
        'LINE LOGIN',
        getPopupWindowSize(),
      );
      const timer = setInterval(() => {
        if (refPopupWindow.current.closed) {
          clearInterval(timer);
          startPolling();
        }
      }, 1000);
    }
  };

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Text
        variant="formLabel"
        className="member-center-profile__label"
        textAlign="left"
      >
        Line
      </Text>
      <Button
        type="button"
        variant="outline"
        onClick={hasSubscribed ? handleUnsubscribe : handleSubscribe}
        display="flex"
        alignItems="center"
        isLoading={subscriptionStatus === SUBSCRIPTION_STATUS_TYPE.LOADING}
        data-e2e-id={e2eId}
        className={classNames(
          'member-center-profile__outline-button',
          hasSubscribed ? 'line-unsubscribe-button' : 'line-subscribe-button',
        )}
      >
        {hasSubscribed ? (
          <>
            <CheckCircle color="#4CC764" />
            <Text variant="subscribeText">
              {t('member_profile.subscribed')}
            </Text>
          </>
        ) : (
          <>
            <Line />
            <Text variant="subscribeText">{t('member_profile.subscribe')}</Text>
          </>
        )}
      </Button>
    </Flex>
  );
};

LineSubscription.propTypes = {
  subscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
    }),
  ),
  notificationType: PropTypes.string.isRequired,
  e2eId: PropTypes.string,
  shouldCheckMemberReward: PropTypes.bool.isRequired,
};

export default LineSubscription;
